import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { deriveRateCard } from "../../services/apiService";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { formatNum, unitForBasis } from "./rateCardConstants";
import { parseDimensions, formatDimsSummary } from "./dimensionParse";
import { cushionEvidenceFlags } from "./costedJobReference";

const DEPTS = ["cnc", "cut", "fab", "paint"];
const EMPTY_DEPTS = { cnc: "", cut: "", fab: "", paint: "" };
const LOW_CONFIDENCE = 0.7;

// Indexed dept hours are PER-UNIT (historical_line_item.py declares the
// contract; the 2026JD corpus honors it). The strip's inputs want line
// totals, so both the dept split and the labor_per_unit fallback multiply
// back up by quantity before prefilling.
function roundHours(value) {
  return Math.round(Number(value) * 100) / 100;
}

function finiteOrNull(value) {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

/**
 * Prefill values from the QUOTED columns of an indexed line — the original
 * costing sheet's own labor, stored per-unit and scaled back to line totals.
 */
function quotedFill(item, qty) {
  const deptValues = { ...EMPTY_DEPTS };
  let hasDept = false;
  DEPTS.forEach((dept) => {
    const numeric = finiteOrNull(item[`${dept}_hours`]);
    if (numeric === null) return;
    // Dept columns hold PER-UNIT hours (the model contract; the 2026JD
    // corpus honors it — legacy dept values are documented as unreliable
    // either way). The derive endpoint wants line totals, so scale up.
    deptValues[dept] = String(roundHours(numeric * qty));
    hasDept = true;
  });
  if (hasDept) return { deptHours: deptValues, totalHours: "" };

  const perUnit = finiteOrNull(item.labor_per_unit);
  if (perUnit !== null) {
    // No department split on the sheet — rebuild the line total from the
    // normalized per-unit figure (labor_per_unit = total ÷ qty).
    return {
      deptHours: { ...EMPTY_DEPTS },
      totalHours: String(roundHours(perUnit * qty)),
    };
  }
  return { deptHours: { ...EMPTY_DEPTS }, totalHours: "" };
}

/**
 * Prefill values from the ACTUAL Island Time hours attached to the line.
 *
 * `actual_labor_detail` department figures are TOTALS for the quantity that
 * shift window covered (`detail.qty`) — not per-unit like the quoted columns.
 * So: per-unit = dept total ÷ detail.qty, then × this line's quantity to get
 * the line totals the derive endpoint wants.
 *
 * Returns null when the line carries no usable actuals, so the caller can fall
 * back to the quoted hours and say so rather than silently filling zeros.
 */
function actualFill(item, qty) {
  const detail = (item && item.actual_labor_detail) || null;
  const deptValues = { ...EMPTY_DEPTS };
  let hasDept = false;
  const detailQty = finiteOrNull(detail && detail.qty);
  if (detail && detailQty !== null && detailQty > 0) {
    DEPTS.forEach((dept) => {
      const total = finiteOrNull(detail[`${dept}_hours`]);
      if (total === null) return;
      deptValues[dept] = String(roundHours((total / detailQty) * qty));
      if (total > 0) hasDept = true;
    });
  }
  if (hasDept) return { deptHours: deptValues, totalHours: "" };

  // No department breakdown from Island Time — fall back to the single
  // clocked per-unit figure the attach script derived.
  const perUnit =
    finiteOrNull(detail && detail.shop_hours_per_unit) ??
    finiteOrNull(item && item.actual_hours_per_unit);
  if (perUnit !== null && perUnit > 0) {
    return {
      deptHours: { ...EMPTY_DEPTS },
      totalHours: String(roundHours(perUnit * qty)),
    };
  }
  return null;
}

/**
 * "Pull rates from a costed job" strip. Given one real item you already know
 * from a job costing sheet — its size, quantity, and shop hours — this calls
 * POST /api/rate-cards/derive and hands the four per-department rates back to
 * the parent form via onApply. It's how Andy invented the method: measure one
 * shutter, divide the hours, and every future shutter prices itself.
 *
 * The costed line arrives as `pickedItem` from the references table directly
 * above these inputs — same section, no navigation, so a pulled row visibly
 * fills the fields here. The strip owns no browse control of its own.
 */
function RateCardDeriveStrip({
  geometryMethod,
  laborBasis,
  onApply,
  pickedItem = null,
}) {
  const [dims, setDims] = useState({ width: "", height: "", length: "" });
  const [quantity, setQuantity] = useState("1");
  const [deptHours, setDeptHours] = useState({ ...EMPTY_DEPTS });
  const [totalHours, setTotalHours] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [source, setSource] = useState(null);

  const isPerPiece = laborBasis === "unit";
  const unit = unitForBasis(laborBasis);

  const setDim = (key, value) => setDims((prev) => ({ ...prev, [key]: value }));
  const setDept = (key, value) =>
    setDeptHours((prev) => ({ ...prev, [key]: value }));

  const hasDeptSplit = DEPTS.some(
    (d) => deptHours[d] !== "" && deptHours[d] !== null,
  );

  /**
   * Fill the strip from an indexed historical costing-sheet line. Everything
   * stays editable afterwards — this is a head start, not a lock-in.
   */
  const handlePickHistorical = (item, laborSource = "quoted") => {
    if (!item) return;
    setError(null);
    setResult(null);

    const qty = Math.max(1, Math.round(Number(item.quantity) || 1));
    setQuantity(String(qty));

    // Scoped to "Actual (Island Time)"? Then the numbers the admin is looking
    // at are shop hours, and those are the numbers that must land in the card.
    // Anything else keeps the quoted-sheet behavior.
    const wantsActual = laborSource === "actual";
    const actual = wantsActual ? actualFill(item, qty) : null;
    const fill = actual || quotedFill(item, qty);
    const usedActual = Boolean(actual);
    setDeptHours(fill.deptHours);
    setTotalHours(fill.totalHours);

    // 2026-corpus rows carry their size in the description rather than in
    // dimensions_raw; an unreadable string parses to null either way, which
    // leaves the inputs blank rather than guessing.
    const parsed =
      parseDimensions(item.dimensions_raw) || parseDimensions(item.description);
    setDims({
      width: parsed && parsed.width !== null ? String(parsed.width) : "",
      height: parsed && parsed.height !== null ? String(parsed.height) : "",
      length: parsed && parsed.length !== null ? String(parsed.length) : "",
    });

    const confidence = Number(item.confidence);
    setSource({
      label: item.job_number || item.source_file || "a historical sheet",
      name: item.name || "",
      dims: formatDimsSummary(parsed),
      quantity: qty,
      usedActual,
      // Asked for actuals, got none — the card is being built off the quote
      // and the admin has to be told, not left to assume.
      actualsMissing: wantsActual && !usedActual,
      // The Island Time caveats: a guessed unit count or a job-level total
      // spread over several lines makes these hours an estimate.
      actualsApproximate: usedActual && cushionEvidenceFlags(item).length > 0,
      // Extraction confidence is a property of the QUOTED sheet parse only.
      lowConfidence:
        !usedActual &&
        Number.isFinite(confidence) &&
        confidence < LOW_CONFIDENCE,
    });

    return {
      dims: {
        width: parsed && parsed.width !== null ? String(parsed.width) : "",
        height: parsed && parsed.height !== null ? String(parsed.height) : "",
        length: parsed && parsed.length !== null ? String(parsed.length) : "",
      },
      quantity: String(qty),
      deptHours: fill.deptHours,
      totalHours: fill.totalHours,
    };
  };

  const clearSource = () => {
    setSource(null);
    setDims({ width: "", height: "", length: "" });
    setQuantity("1");
    setDeptHours({ ...EMPTY_DEPTS });
    setTotalHours("");
    setError(null);
    setResult(null);
  };

  const handleDerive = async (pickedValues = null) => {
    const values = pickedValues || {
      dims,
      quantity,
      deptHours,
      totalHours,
    };
    const valuesHaveDeptSplit = DEPTS.some(
      (d) => values.deptHours[d] !== "" && values.deptHours[d] !== null,
    );
    setError(null);
    setResult(null);
    if (!isPerPiece && (!values.dims.width || !values.dims.height)) {
      setError("Enter at least width and height.");
      return;
    }
    if (!valuesHaveDeptSplit && !values.totalHours) {
      setError(
        "Enter the four department hours or a single total-hours value.",
      );
      return;
    }
    setLoading(true);
    try {
      const payload = {
        width: Number(values.dims.width) || 0,
        height: Number(values.dims.height) || 0,
        length: Number(values.dims.length) || 0,
        geometry_method: geometryMethod,
        labor_basis: laborBasis,
        quantity: Number(values.quantity) || 1,
        dept_hours: valuesHaveDeptSplit
          ? {
              cnc:
                values.deptHours.cnc === "" ? 0 : Number(values.deptHours.cnc),
              cut:
                values.deptHours.cut === "" ? 0 : Number(values.deptHours.cut),
              fab:
                values.deptHours.fab === "" ? 0 : Number(values.deptHours.fab),
              paint:
                values.deptHours.paint === ""
                  ? 0
                  : Number(values.deptHours.paint),
            }
          : null,
        total_hours: valuesHaveDeptSplit
          ? null
          : Number(values.totalHours) || 0,
      };
      const data = await deriveRateCard(payload);
      setResult(data);
      if (data.rates) {
        onApply(data.rates);
      }
    } catch (err) {
      setError(
        err.error || err.message || "Couldn't pull rates from those numbers.",
      );
    } finally {
      setLoading(false);
    }
  };

  // A line pulled on the Costed Job References screen lands here. Same entry
  // point as any other pick — one prefill path, no second copy of the mapping.
  //
  // The prop is either the indexed line itself or a { item, pullId,
  // laborSource } envelope. The envelope exists so the caller can re-pull the
  // SAME row after the strip was cleared: a new pullId is a new object, so this
  // effect fires again, where a bare repeat of the identical item reference
  // would be a no-op. `laborSource` carries the scope the row was read under.
  // The inspector's explicit Pull rates action also sets autoApply so this
  // one handoff both fills the editable evidence fields and updates the card.
  useEffect(() => {
    if (!pickedItem) return;
    const isEnvelope = Object.prototype.hasOwnProperty.call(pickedItem, "item");
    const pickedValues = handlePickHistorical(
      isEnvelope ? pickedItem.item : pickedItem,
      isEnvelope ? pickedItem.laborSource : "quoted",
    );
    if (isEnvelope && pickedItem.autoApply) {
      handleDerive(pickedValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedItem]);

  return (
    <div>
      {/* One line, not a lecture: where the numbers come from, and that typing
          them yourself is a first-class path. Replaced by the provenance card
          the moment a row above is pulled. */}
      {!source && (
        <p className="text-sm text-[var(--text-color)] opacity-80">
          {isPerPiece
            ? "Pull rates from a line above, or type one job's quantity and shop hours in yourself — hours ÷ quantity becomes the per-piece rates."
            : "Pull rates from a line above, or type one job's size, quantity and shop hours in yourself — they become the per-square-foot department rates."}
        </p>
      )}

      {source && (
        <div className="flex items-start gap-2 rounded-md border border-[var(--border-color)] bg-[var(--card-bg)] px-3 py-2 text-sm text-[var(--text-color)]">
          <div className="flex-1">
            <span className="opacity-70">
              {result && result.rates ? "Rates pulled from " : "Pulled from "}
            </span>
            <span className="font-semibold">{source.label}</span>
            <span className="opacity-70">
              {" — "}
              {[
                source.name,
                source.dims,
                `${source.quantity} unit${source.quantity === 1 ? "" : "s"}`,
              ]
                .filter(Boolean)
                .join(", ")}
            </span>
            {/* Name the well the hours came from. A card built off shop
                actuals and a card built off the old quote are different
                animals, and the provenance line is where that is said. */}
            <span className="opacity-70">
              {source.usedActual ? " (Island Time actuals)" : " (quoted hours)"}
            </span>
            {source.actualsMissing && (
              <span className="mt-1 block text-xs opacity-60">
                No Island Time hours on this line — filled from the quoted hours
                instead.
              </span>
            )}
            {source.actualsApproximate && (
              <span className="mt-1 block text-xs opacity-60">
                Island Time evidence is approximate — check the numbers.
              </span>
            )}
            {source.lowConfidence && (
              <span className="mt-1 block text-xs opacity-60">
                Low-confidence extraction — check the numbers.
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={clearSource}
            aria-label="Clear the picked historical line"
            title="Clear the picked historical line"
            className="wb-plain rounded p-1 text-[var(--text-color)] opacity-60 transition-colors hover:bg-[var(--hover-bg)] hover:opacity-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {!isPerPiece && (
        <div className="mt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-color)] opacity-70">
            Item size
          </p>
          <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <LabeledInput label="Width (in)">
              <Input
                type="number"
                min="0"
                step="0.25"
                value={dims.width}
                onChange={(e) => setDim("width", e.target.value)}
                onFocus={(e) => e.target.select()}
                className="h-9 text-right tabular-nums"
              />
            </LabeledInput>
            <LabeledInput label="Height (in)">
              <Input
                type="number"
                min="0"
                step="0.25"
                value={dims.height}
                onChange={(e) => setDim("height", e.target.value)}
                onFocus={(e) => e.target.select()}
                className="h-9 text-right tabular-nums"
              />
            </LabeledInput>
            <LabeledInput label="Length (in)">
              <Input
                type="number"
                min="0"
                step="0.25"
                value={dims.length}
                onChange={(e) => setDim("length", e.target.value)}
                onFocus={(e) => e.target.select()}
                className="h-9 text-right tabular-nums"
              />
            </LabeledInput>
            <LabeledInput label="Quantity on that job">
              <Input
                type="number"
                min="1"
                step="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                onFocus={(e) => e.target.select()}
                className="h-9 text-right tabular-nums"
              />
            </LabeledInput>
          </div>
        </div>
      )}

      {isPerPiece && (
        <div className="mt-4 max-w-[12rem]">
          <LabeledInput label="Quantity on that job">
            <Input
              type="number"
              min="1"
              step="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              onFocus={(e) => e.target.select()}
              className="h-9 text-right tabular-nums"
            />
          </LabeledInput>
        </div>
      )}

      <div className="mt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-color)] opacity-70">
          Shop hours for the whole line (by department)
        </p>
        <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {DEPTS.map((dept) => (
            <LabeledInput key={dept} label={`${dept.toUpperCase()} hrs`}>
              <Input
                type="number"
                min="0"
                step="0.25"
                value={deptHours[dept]}
                onChange={(e) => setDept(dept, e.target.value)}
                onFocus={(e) => e.target.select()}
                className="h-9 text-right tabular-nums"
              />
            </LabeledInput>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <LabeledInput label="…or just the line's total hours">
          <Input
            type="number"
            min="0"
            step="0.25"
            value={totalHours}
            disabled={hasDeptSplit}
            onChange={(e) => setTotalHours(e.target.value)}
            onFocus={(e) => e.target.select()}
            className="h-9 w-32 text-right tabular-nums"
          />
        </LabeledInput>
        <Button
          type="button"
          size="sm"
          onClick={() => handleDerive()}
          disabled={loading}
        >
          {loading ? "Pulling…" : "Pull rates into the card"}
        </Button>
      </div>

      {error && (
        <p className="mt-3 text-sm text-[color:var(--error-color)]">{error}</p>
      )}

      {result && (
        <div className="mt-3 rounded-md border border-[var(--border-color)] bg-[var(--card-bg)] px-3 py-2 text-sm text-[var(--text-color)]">
          {result.rates ? (
            <span>
              {!isPerPiece && (
                <span className="font-semibold tabular-nums">
                  {formatNum(result.sqft, 1)} sqft{" "}
                </span>
              )}
              <span className="opacity-70">→ </span>
              <span className="tabular-nums">
                CNC {formatNum(result.rates.cnc, 3)} · Cut{" "}
                {formatNum(result.rates.cut, 3)} · Fab{" "}
                {formatNum(result.rates.fab, 3)} · Paint{" "}
                {formatNum(result.rates.paint, 3)} {unit}
              </span>
              <span className="opacity-70">
                {" "}
                — rates filled below, adjust freely.
              </span>
            </span>
          ) : (
            <span>
              {!isPerPiece && (
                <span className="opacity-70">
                  {formatNum(result.sqft, 1)} sqft ·{" "}
                </span>
              )}
              Total rate{" "}
              <span className="font-semibold tabular-nums">
                {formatNum(result.total_rate, 5)}
              </span>{" "}
              {unit}. Enter the department split above to fill individual rates
              — or type them into the card by feel.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function LabeledInput({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-[var(--text-color)] opacity-70">
        {label}
      </span>
      {children}
    </label>
  );
}

export default RateCardDeriveStrip;
